/**
 * Consent audit / verification mechanism.
 *
 * Guarantees that — even when the external CMP script never loads — the
 * stored cookie preferences are actually applied to Google Consent Mode v2
 * and that ads/analytics are activated (deferred) exactly once consent allows.
 *
 * Exposes `window.__consentAudit()` so e2e tests (and support) can inspect the
 * effective state without reading internals.
 */

export interface ConsentState {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

export interface ConsentAuditResult {
  hasStoredConsent: boolean;
  stored: ConsentState | null;
  storedAt: string | null;
  expired: boolean;
  cmpExternalActive: boolean;
  cmpUnavailable: boolean;
  applied: {
    analytics_storage: 'granted' | 'denied';
    ad_storage: 'granted' | 'denied';
    ad_user_data: 'granted' | 'denied';
    ad_personalization: 'granted' | 'denied';
  } | null;
  consistent: boolean;
  deferredActivated: boolean;
  issues: string[];
}

const CONSENT_EXPIRY_MS = 6 * 30 * 24 * 60 * 60 * 1000;
const STORAGE_KEY = 'cookie_consent';
const STORAGE_DATE_KEY = 'cookie_consent_date';

type AuditWindow = Window & {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
  __ENABLE_EXTERNAL_CMP?: boolean;
  __CMP_UNAVAILABLE?: boolean;
  __consentAudit?: () => ConsentAuditResult;
  __consentApplied?: ConsentAuditResult['applied'];
  __consentDeferredActivated?: boolean;
};

const readStored = (): { state: ConsentState | null; date: string | null; expired: boolean } => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const date = localStorage.getItem(STORAGE_DATE_KEY);
    if (!raw || !date) return { state: null, date: null, expired: false };
    const expired = Date.now() - new Date(date).getTime() > CONSENT_EXPIRY_MS;
    return { state: JSON.parse(raw) as ConsentState, date, expired };
  } catch {
    return { state: null, date: null, expired: false };
  }
};

const toSignals = (state: ConsentState) => ({
  analytics_storage: (state.analytics ? 'granted' : 'denied') as 'granted' | 'denied',
  ad_storage: (state.marketing ? 'granted' : 'denied') as 'granted' | 'denied',
  ad_user_data: (state.marketing ? 'granted' : 'denied') as 'granted' | 'denied',
  ad_personalization: (state.marketing ? 'granted' : 'denied') as 'granted' | 'denied',
});

/** Applies a consent state to Consent Mode v2 + dataLayer and records it for auditing. */
export const applyConsentState = (state: ConsentState, source: 'restore' | 'user' | 'cmp' = 'user') => {
  if (typeof window === 'undefined') return;
  const w = window as AuditWindow;
  const signals = toSignals(state);

  if (typeof w.gtag === 'function') {
    w.gtag('consent', 'update', signals);
  }
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({
    event: 'consent_verified',
    consent_source: source,
    consent_analytics: signals.analytics_storage,
    consent_marketing: signals.ad_storage,
  });
  w.__consentApplied = signals;

  // Deferred activation: only fire marketing pixels once marketing is granted.
  if (state.marketing && !w.__consentDeferredActivated) {
    w.__consentDeferredActivated = true;
    w.dataLayer.push({ event: 'marketing_consent_activated' });
    // Meta PageView is fired by the route tracker (src/lib/metaPixel.ts).
  }
};

/** Inspects stored vs. applied consent and reports inconsistencies. */
export const auditConsent = (): ConsentAuditResult => {
  const w = window as AuditWindow;
  const { state, date, expired } = readStored();
  const applied = w.__consentApplied ?? null;
  const issues: string[] = [];

  if (state && !expired) {
    const expected = toSignals(state);
    if (!applied) {
      issues.push('stored_consent_not_applied');
    } else if (
      applied.analytics_storage !== expected.analytics_storage ||
      applied.ad_storage !== expected.ad_storage
    ) {
      issues.push('applied_consent_mismatch');
    }
    if (state.marketing && !w.__consentDeferredActivated) {
      issues.push('marketing_not_activated');
    }
    if (!state.marketing && w.__consentDeferredActivated) {
      issues.push('marketing_activated_without_consent');
    }
  }
  if (expired) issues.push('consent_expired');

  return {
    hasStoredConsent: Boolean(state) && !expired,
    stored: state,
    storedAt: date,
    expired,
    cmpExternalActive: Boolean(w.__ENABLE_EXTERNAL_CMP) && !w.__CMP_UNAVAILABLE,
    cmpUnavailable: Boolean(w.__CMP_UNAVAILABLE),
    applied,
    consistent: issues.length === 0,
    deferredActivated: Boolean(w.__consentDeferredActivated),
    issues,
  };
};

let started = false;

/**
 * Restores and re-applies stored consent on boot (self-healing when the CMP
 * script is blocked/offline), then exposes the audit helper.
 */
export const startConsentAudit = () => {
  if (started || typeof window === 'undefined') return;
  started = true;
  const w = window as AuditWindow;
  w.__consentAudit = auditConsent;

  const restore = () => {
    const { state, expired } = readStored();
    if (state && !expired) {
      applyConsentState(state, 'restore');
    } else if (expired) {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_DATE_KEY);
    }
  };

  restore();

  // Keep the audit in sync with in-app consent changes and CMP fallbacks.
  window.addEventListener('consent_updated', (event) => {
    const detail = (event as CustomEvent<ConsentState>).detail;
    if (detail) applyConsentState(detail, 'user');
  });
  window.addEventListener('cmp_unavailable', restore);
};
